// @flow
import { createRef } from 'react';

import QuestionMarkPopup from '../popups/QuestionMark';
import * as PricePerCaratContent from '../popups/content/PricePerCarat';
import * as CaratWeightContent from '../popups/content/CaratWeight';
import * as CutContent from '../popups/content/Cut';
import * as ColorContent from '../popups/content/Color';
import * as ClarityContent from '../popups/content/Clarity';
import * as PolishContent from '../popups/content/Polish';
import * as SymmetryContent from '../popups/content/Symmetry';
import * as GiaContent from '../popups/content/Gia';
import * as LengthToWidthRatioContent from '../popups/content/LengthToWidthRatio';
import * as DiamondDimensionsContent from '../popups/content/DiamondDimensions';
import * as DepthContent from '../popups/content/Depth';
import * as TableContent from '../popups/content/Table';
import * as FluorescenceContent from '../popups/content/Fluorescence';
import * as CuletContent from '../popups/content/Culet';
import * as GirdleContent from '../popups/content/Girdle';

import type { ObjectType } from '../../types/misc';

const popups = {
  Price_per_carat: PricePerCaratContent,
  Carat_Weight: CaratWeightContent,
  Cut: CutContent,
  Color: ColorContent,
  Clarity: ClarityContent,
  Polish: PolishContent,
  Symmetry: SymmetryContent,
  Lab: GiaContent,
  LW_Ratio: LengthToWidthRatioContent,
  DiamondDimensions: DiamondDimensionsContent,
  Depth_: DepthContent,
  Table_: TableContent,
  Fluorescence: FluorescenceContent,
  Culet: CuletContent,
  Girdle: GirdleContent
};

const underscore: string => string = string =>
  string
    .replace(/.[-_]?[A-Z]/g, match => `_${match.toLowerCase()}`)
    .toLowerCase();
const makeBubbleName: string => string = name => underscore(name);

type PropsType = {
  name: string,
  bubbleName?: string,
  onNameClick: (N: string) => void,
  sortColumn?: string,
  sortDirection?: string
};

type StateType = {
  iconIsShowing: boolean
};

export default class CompareBubble extends React.Component<
  PropsType,
  StateType
> {
  containerRef: { current: null | HTMLElement };
  mouseIsOverRef: { current: null | boolean };
  popupIsShowingRef: { current: null | boolean };

  state = { iconIsShowing: false };

  constructor(props: PropsType, opts: ObjectType) {
    super(props, opts);

    this.containerRef = createRef();
    this.mouseIsOverRef = createRef();
    this.popupIsShowingRef = createRef();
    this.mouseIsOverRef.current = false;
    this.popupIsShowingRef.current = false;
  }

  handleMouseEnter = () => {
    // clicking a link in a popup bubble was triggering a second mouseenter
    // event which was re-rendering the popup, causing it to jump to the top
    // of the bubble due to the popups/Base scrolling up when re-rendering popup
    // content.
    if (this.mouseIsOverRef.current) return;

    this.mouseIsOverRef.current = true;
    this.setState({ iconIsShowing: true });
  };

  handleMouseLeave = e => {
    this.mouseIsOverRef.current = false;

    if (this.popupIsShowingRef.current) return;
    if (this.mouseIsOverRef.current) return;

    this.setState({ iconIsShowing: false });
  };

  popupShown = () => {
    this.popupIsShowingRef.current = true;
  };

  popupClosed = () => {
    this.popupIsShowingRef.current = false;

    if (this.popupIsShowingRef.current) return;
    if (this.mouseIsOverRef.current) return;

    this.setState({ iconIsShowing: false });
  };

  handleDivClick = (bubbleName: string) => {
    this.props.onNameClick(bubbleName);
  };

  cleanName = (text: string) => {
    let { name } = this.props;

    return (text || name)
      .replace(/\s/g, '_')
      .replace(/[^\w-_]/g, '')
      .trim();
  };

  divTag = () => {
    let { name, bubbleName } = this.props;
    const cleanName = this.cleanName();
    const forcedBubbleName = bubbleName || makeBubbleName(cleanName);
    const isActive = this.props.sortColumn === forcedBubbleName;
    const isDescending = this.props.sortDirection === 'desc';

    const className = isActive
      ? isDescending
        ? 'active_desc'
        : 'active'
      : null;

    return (
      <div
        onClick={() => this.handleDivClick(forcedBubbleName)}
        className={`${className} compare-button`}
      >
        {name}
      </div>
    );
  };

  render() {
    const popupName = this.cleanName(this.props.popupName);

    return (
      <div
        onMouseEnter={this.handleMouseEnter}
        onFocus={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        style={{ position: 'relative' }}
        ref={this.containerRef}
      >
        {this.divTag()}
        {this.state.iconIsShowing && popups[popupName] ? (
          <QuestionMarkPopup
            initialContent={popups[popupName]}
            style={{ width: 20, height: 20 }}
            containerStyle={{ position: 'absolute', top: 5, left: -21 }}
            className='popup-bubble'
            onShow={this.popupShown}
            onClose={this.popupClosed}
            whiteBackground
            preferSideAttachment
            popupLocationOptions={{ fitVertically: true }}
          />
        ) : null}
      </div>
    );
  }
}
